import type { ScheduleOrAnalysis } from "@/pages/human-resources/time-management/components/switch-schedule-analysis/SwitchScheduleAnalysis.tsx";
import type { ScheduleRange } from "@/pages/human-resources/time-management/model/ScheduleRange.ts";
import { useCallback } from "react";
import { useRouteParam } from "@/shared/routing/useRouteParam.ts";
import { DateTime } from "luxon";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";
import useDojoScheduleUrlBuilder from "@/pages/human-resources/time-management/hooks/useDojoScheduleUrlBuilder.ts";
import useDomainSiteOnRoute from "@/shared/routing/useDomainSiteOnRoute.ts";
import useScheduleDataState from "@/pages/human-resources/time-management/schedules/site-schedule/model/schedule-data/useScheduleDataState.ts";
import useScheduleRangeOnRoute from "@/pages/human-resources/time-management/hooks/useScheduleRangeOnRoute.ts";

interface ScheduleNavigation {
  toView: (view: ScheduleOrAnalysis) => string;
  toDate: (date: Date) => string;
  toRange: (view: ScheduleRange) => string;
}

export default function useScheduleUrls(
  currentViewType: ScheduleOrAnalysis,
): ScheduleNavigation {
  const dojoUrlBuilder = useDojoScheduleUrlBuilder();
  const { startDate } = useScheduleDataState();
  const { site } = useDomainSiteOnRoute();
  const currentRange = useScheduleRangeOnRoute();
  const currentDateStr = useRouteParam("date");

  const buildReactUrl = useCallback(
    (
      view: ScheduleOrAnalysis,
      range: ScheduleRange,
      dateStr: string,
    ): string => {
      if (site === undefined) return "";
      const basePath = `/hr/time/schedules/domain/${site.domainId}/site/${site.id}`;
      const analysisOrSlash = view === "analysis" ? "/analysis/" : "/";
      return `${basePath}${analysisOrSlash}${range.toLowerCase()}/${dateStr}`;
    },
    [site],
  );

  const toView = useCallback(
    (view: ScheduleOrAnalysis) =>
      buildReactUrl(view, currentRange, currentDateStr),
    [buildReactUrl, currentDateStr, currentRange],
  );

  const toDate = useCallback(
    (date: Date) => {
      const dateStr = DateTime.fromJSDate(date)
        .setZone(LEGACY_TIME_ZONE)
        .toFormat("yyyy-MM-dd");
      return buildReactUrl(currentViewType, currentRange, dateStr);
    },
    [buildReactUrl, currentRange, currentViewType],
  );

  const toRange = useCallback(
    (range: ScheduleRange): string => {
      switch (range) {
        case "DAY": {
          return buildReactUrl(currentViewType, range, currentDateStr);
        }
        case "WEEK": {
          return dojoUrlBuilder(site, "DRAFT", range, startDate) ?? "";
        }
        case "MONTH": {
          const monthStartStr = DateTime.fromISO(currentDateStr, {
            zone: LEGACY_TIME_ZONE,
          })
            .set({ day: 1 })
            .toFormat("yyyy-MM-dd");
          return buildReactUrl(currentViewType, range, monthStartStr);
        }
      }
    },
    [
      buildReactUrl,
      currentDateStr,
      currentViewType,
      dojoUrlBuilder,
      site,
      startDate,
    ],
  );

  return { toView, toDate, toRange };
}
